import { memo, useMemo } from "react";

import { Markdown } from "@/components/molecules/Markdown";
import {
  groupLinesWithReferences,
  splitTextBlocks,
} from "./ToolCallCard.tools";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Objects } from "@/objects";
import { ObjectType } from "@/objects/@types";
import { idToIdentifier } from "@/objects/@utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Typography } from "@/components/ui/typography";

// ---------------------------------------------------------------------------
// Data table utilities
// ---------------------------------------------------------------------------

function formatHeader(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatCellValue(value: unknown, fieldType?: string): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (fieldType === "currency" && typeof value === "string") {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(num);
    }
  }
  if (fieldType === "date" && typeof value === "string") {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date.toLocaleDateString();
  }
  if (typeof value === "object") {
    if (Array.isArray(value)) return `[${value.length} items]`;
    if ("name" in value && (value as Record<string, unknown>).name)
      return String((value as Record<string, unknown>).name);
    if ("identifier" in value)
      return String((value as Record<string, unknown>).identifier);
    return JSON.stringify(value);
  }
  return String(value);
}

function getColumnsFromObjectDef(
  typename: string,
): { key: string; label: string; type: string }[] | null {
  const objectDef = Objects[typename as keyof typeof Objects] as
    | ObjectType<any>
    | undefined;
  if (!objectDef?.fieldDefinitions) return null;
  const simpleTypes = [
    "text",
    "string",
    "currency",
    "date",
    "datetime",
    "number",
    "enum",
  ];
  return objectDef.fieldDefinitions
    .filter(
      (field) =>
        simpleTypes.includes(field.type) && field.objectKey !== "entity",
    )
    .slice(0, 6)
    .map((field) => ({
      key: String(field.objectKey),
      label: field.label,
      type: field.type,
    }));
}

export function generateMarkdownTable(
  items: Record<string, unknown>[],
): string {
  if (!items || items.length === 0) return "";
  const priorityKeys = [
    "name",
    "identifier",
    "description",
    "amount",
    "type",
    "status",
  ];
  const allKeys = new Set<string>();
  items.forEach((item) => Object.keys(item).forEach((key) => allKeys.add(key)));
  const filteredKeys = Array.from(allKeys).filter(
    (key) => !key.startsWith("_") && key !== "id" && key !== "__typename",
  );
  const sortedKeys = [
    ...priorityKeys.filter((k) => filteredKeys.includes(k)),
    ...filteredKeys.filter((k) => !priorityKeys.includes(k)),
  ].slice(0, 5);

  const header = `| ${sortedKeys.map(formatHeader).join(" | ")} |`;
  const separator = `| ${sortedKeys.map(() => "---").join(" | ")} |`;
  const rows = items
    .slice(0, 10)
    .map(
      (item) =>
        `| ${sortedKeys.map((key) => formatCellValue(item[key])).join(" | ")} |`,
    );

  let table = [header, separator, ...rows].join("\n");
  if (items.length > 10) {
    table += `\n\n*... and ${items.length - 10} more items*`;
  }
  return table;
}

const TABLE_CLASS =
  "border text-[11px] [&_th]:h-auto [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:bg-accent [&_th]:font-semibold [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_td]:max-w-50 [&_td]:overflow-hidden [&_td]:text-ellipsis";

export function TypedDataTable({
  items,
  typename,
  maxRows = 10,
}: {
  items: Record<string, unknown>[];
  typename: string;
  maxRows?: number;
}) {
  const columns = getColumnsFromObjectDef(typename);
  const objectDef = Objects[typename as keyof typeof Objects] as
    | { getEntityId?: (data: Record<string, unknown>) => string }
    | undefined;
  const hasEntityColumn = Boolean(objectDef?.getEntityId);

  if (!columns || columns.length === 0) {
    return (
      <GenericDataTable items={items} typename={typename} maxRows={maxRows} />
    );
  }

  const displayItems = items.slice(0, maxRows);
  const hasMore = items.length > maxRows;

  return (
    <div className="overflow-auto max-h-75">
      <Table className={TABLE_CLASS}>
        <TableHeader>
          <TableRow>
            {hasEntityColumn && <TableHead style={{ width: 32 }} />}
            {columns.map((col) => (
              <TableHead key={col.key}>{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayItems.map((item, index) => {
            const entityId = objectDef?.getEntityId?.(item);
            const entityIdentifier = entityId ? idToIdentifier(entityId) : null;
            return (
              <TableRow key={(item.id as string) || index}>
                {hasEntityColumn && (
                  <TableCell
                    style={{
                      width: 32,
                      padding: "4px 8px",
                      verticalAlign: "middle",
                    }}
                  >
                    {entityIdentifier ? (
                      <BoxForEntity
                        identifier={entityIdentifier}
                        representation="character"
                        size="xs"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    title={formatCellValue(item[col.key], col.type)}
                  >
                    {formatCellValue(item[col.key], col.type)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {hasMore && (
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 mt-1 text-center"
        >
          ... and {items.length - maxRows} more items
        </Typography>
      )}
    </div>
  );
}

export function GenericDataTable({
  items,
  typename,
  maxRows = 10,
}: {
  items: Record<string, unknown>[];
  typename?: string;
  maxRows?: number;
}) {
  if (!items || items.length === 0) return null;

  const objectDef = typename
    ? (Objects[typename as keyof typeof Objects] as
        | { getEntityId?: (data: Record<string, unknown>) => string }
        | undefined)
    : undefined;
  const hasEntityColumn = Boolean(objectDef?.getEntityId);

  const priorityKeys = [
    "name",
    "identifier",
    "description",
    "amount",
    "type",
    "status",
    "created_at",
  ];
  const allKeys = new Set<string>();
  items.forEach((item) => Object.keys(item).forEach((key) => allKeys.add(key)));
  const filteredKeys = Array.from(allKeys).filter(
    (key) => !key.startsWith("_") && key !== "id" && key !== "__typename",
  );
  const sortedKeys = [
    ...priorityKeys.filter((k) => filteredKeys.includes(k)),
    ...filteredKeys.filter((k) => !priorityKeys.includes(k)),
  ].slice(0, 6);

  const displayItems = items.slice(0, maxRows);
  const hasMore = items.length > maxRows;

  return (
    <div className="overflow-auto max-h-75">
      <Table className={TABLE_CLASS}>
        <TableHeader>
          <TableRow>
            {hasEntityColumn && <TableHead style={{ width: 32 }} />}
            {sortedKeys.map((key) => (
              <TableHead key={key}>{formatHeader(key)}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayItems.map((item, index) => {
            const entityId = objectDef?.getEntityId?.(item);
            const entityIdentifier = entityId ? idToIdentifier(entityId) : null;
            return (
              <TableRow key={index}>
                {hasEntityColumn && (
                  <TableCell
                    style={{
                      width: 32,
                      padding: "4px 8px",
                      verticalAlign: "middle",
                    }}
                  >
                    {entityIdentifier ? (
                      <BoxForEntity
                        identifier={entityIdentifier}
                        representation="character"
                        size="xs"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                {sortedKeys.map((key) => (
                  <TableCell key={key} title={formatCellValue(item[key])}>
                    {formatCellValue(item[key])}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {hasMore && (
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 mt-1 text-center"
        >
          ... and {items.length - maxRows} more items
        </Typography>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Text content with inline reference rendering
// ---------------------------------------------------------------------------

// Pattern to match reference syntax: [[ref:TypeName:identifier:DisplayName]]
// Also captures optional **bold** markers around the reference.
// Use a factory so each call site gets its own stateful regex instance,
// eliminating the global-regex lastIndex bug that existed in the original.
const REFERENCE_PATTERN_SRC = String.raw`\*{0,2}\[\[ref:([^:]+):([^:]+):([^\]]+)\]\]\*{0,2}`;

function paragraphHasReferences(paragraph: string): boolean {
  return new RegExp(REFERENCE_PATTERN_SRC).test(paragraph);
}

function makeReferencePattern(): RegExp {
  return new RegExp(REFERENCE_PATTERN_SRC, "g");
}

type TextSegment =
  | { type: "text"; content: string }
  | { type: "ref"; typename: string; identifier: string; name: string };

function parseTextWithReferences(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  const pattern = makeReferencePattern();
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
      });
    }
    segments.push({
      type: "ref",
      typename: match[1],
      identifier: match[2],
      name: match[3],
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments;
}

function processInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const matched = match[0];
    if (matched.startsWith("**") && matched.endsWith("**")) {
      parts.push(<strong key={keyCounter++}>{matched.slice(2, -2)}</strong>);
    } else if (matched.startsWith("*") && matched.endsWith("*")) {
      parts.push(<em key={keyCounter++}>{matched.slice(1, -1)}</em>);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : [text];
}

const MARKDOWN_SECONDARY_STYLE = {
  color: "var(--muted-foreground) !important",
} as const;

// Room under each paragraph, and under each line of one that names a record.
// Each is drawn on its own, so the last paragraph of each drops its margin
// (``Markdown`` does that to end flush) and they sat on top of one another. A
// margin rather than a gap, so a table's own larger margin takes its place.
const LINES_CLASS = "[&>*+*]:mt-2";

function LineWithReferencesInner({ line }: { line: string }) {
  const segments = useMemo(() => parseTextWithReferences(line), [line]);
  const hasReferences = segments.some((s) => s.type === "ref");

  if (!hasReferences) {
    return (
      <Markdown style={MARKDOWN_SECONDARY_STYLE} resize={0}>
        {line}
      </Markdown>
    );
  }

  return (
    <span style={{ color: "var(--muted-foreground)" }}>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          return (
            <span key={index}>{processInlineMarkdown(segment.content)}</span>
          );
        }
        const base64Id = btoa(
          `${segment.typename}Object:${segment.identifier}`,
        );
        return (
          <span
            key={index}
            style={{
              position: "relative",
              top: "3px",
              transform: "scale(0.4)",
            }}
          >
            <FieldReference
              value={{
                __typename: `${segment.typename}Object`,
                name: segment.name,
                id: base64Id,
                identifier: segment.identifier,
              }}
              valueOptions={{ style: "related", isNullable: false }}
              isMultiline={false}
              size="sm"
            />
          </span>
        );
      })}
    </span>
  );
}

export const LineWithReferences = memo(LineWithReferencesInner);

function TextWithReferencesInner({ text }: { text: string }) {
  const blocks = useMemo(() => splitTextBlocks(text), [text]);

  return (
    <div className={LINES_CLASS} style={{ color: "var(--muted-foreground)" }}>
      {blocks.map((block, index) => {
        const hasRefs = paragraphHasReferences(block);

        if (!hasRefs) {
          return (
            <div key={index}>
              <Markdown style={MARKDOWN_SECONDARY_STYLE} resize={0}>
                {block}
              </Markdown>
            </div>
          );
        }

        const groups = groupLinesWithReferences(block.split("\n"));

        return (
          <div key={index} className={LINES_CLASS}>
            {groups.map((group, groupIndex) => {
              if (
                (group.type === "table" || group.type === "markdown") &&
                !paragraphHasReferences(group.content)
              ) {
                return (
                  <div key={groupIndex}>
                    <Markdown style={MARKDOWN_SECONDARY_STYLE} resize={0}>
                      {group.content}
                    </Markdown>
                  </div>
                );
              }
              return (
                <div key={groupIndex}>
                  <LineWithReferences line={group.content} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export const TextWithReferences = memo(TextWithReferencesInner);
